<template>
  <div class="label-input">
    <div class="dropdown">
      <Popper
        v-model:open="open"
        placement="bottom-end"
      >
        <template #trigger>
          <div class="dropdown__trigger">
            <div class="label-input__select">
              <span
                v-if="modelValue.filter(x => x).length === 0"
                class="label-input__empty typo-small-title"
              >
                {{ placeholder }}
              </span>

              <div class="label-input__labels">
                <span
                  v-for="label in modelValue.filter(x => x)"
                  :key="label"
                  class="label-input__label typo-small-title"
                >
                  {{ label }}
                </span>
              </div>

              <span class="label-input__select-arrow">
                <UseSvg
                  class="icon icon--small"
                  name="arrow_down_16"
                />
              </span>
            </div>
          </div>
        </template>

        <div class="label-input__dropdown menu menu--dropdown">
          <div class="label-input__dropdown-header">
            <button
              class="button button--small button--light typo-ignore typo-small"
              type="button"
              @click="newLabel"
            >
              <UseSvg
                class="icon icon--small button__icon"
                name="plus"
              />
              <span class="button__label">{{ $t('tasks.new_label') }}</span>
            </button>
            <button
              class="button button--small button--primary typo-ignore typo-small"
              type="button"
              @click="open = false"
            >
              {{ $t('general.done') }}
            </button>
          </div>
          <div
            ref="checkboxes"
            class="label-input__checkboxes"
          >
            <div
              v-for="(label, index) in newOptions"
              :key="index"
              class="checkbox checkbox--gray checkbox--small label-input__checkbox"
            >
              <input
                checked
                class="checkbox__input"
                readonly
                type="checkbox"
              >
              <label class="checkbox__label typo-ignore typo-title">
                <input
                  v-model="newOptions[index]"
                  class="label-input__label-input input"
                  type="text"
                  @input="updateNew(index)"
                >
                <button
                  :aria-label="$t('general.remove')"
                  class="button button--small button--light"
                  type="button"
                  @click="remove(index)"
                >
                  <UseSvg
                    class="icon icon--small"
                    name="trash_16"
                  />
                </button>
              </label>
            </div>
            <div
              v-for="label in options"
              :key="label"
              class="checkbox checkbox--gray checkbox--small label-input__checkbox"
            >
              <input
                :id="`${id}_${label}`"
                :checked="checked(label)"
                class="checkbox__input"
                type="checkbox"
                @change="update(label, $event.target.checked)"
              >
              <label
                class="checkbox__label typo-ignore typo-title"
                :for="`${id}_${label}`"
              >
                {{ label }}
              </label>
            </div>
          </div>
          <div
            v-if="empty"
            class="label-input__dropdown-empty"
          >
            <p class="typo-ignore typo-small">
              <strong>{{ $t('tasks.no_labels') }}</strong>
            </p>
            <p class="typo-ignore typo-small">
              {{ $t('tasks.no_labels_description') }}
            </p>
          </div>
        </div>
      </Popper>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, reactive, ref } from 'vue';
import { v4 as uuid } from 'uuid';

import Popper from '@/components/generic/popper.vue';
import UseSvg from '@/components/generic/use_svg.vue';

const props = defineProps({
  modelValue: { type: Array, default: () => [] },
  options: { type: Array, required: true },
  id: { type: String, default: uuid },
  placeholder: { type: String, default: '' },
});
const emit = defineEmits(['update:modelValue']);

const newOptions = reactive([]);
const open = ref(false);
const checkboxes = ref(null);

const empty = computed(() => {
  return props.options.length === 0 && props.modelValue.length === 0 && newOptions.length === 0;
});

async function newLabel() {
  newOptions.unshift('');
  emit('update:modelValue', ['', ...props.modelValue]);

  await nextTick();
  checkboxes.value.querySelector('input[type=text]').focus();
}

function checked(label) {
  return props.modelValue.includes(label);
}

function update(label, value) {
  if (value) {
    emit('update:modelValue', [...props.modelValue, label]);
  } else {
    emit('update:modelValue', [...props.modelValue.filter(x => x !== label)]);
  }
}

function updateNew(index) {
  emit('update:modelValue', [
    ...props.modelValue.slice(0, index),
    newOptions[index],
    ...props.modelValue.slice(index + 1),
  ]);
}

function remove(index) {
  newOptions.splice(index, 1);
  emit('update:modelValue', [
    ...props.modelValue.slice(0, index),
    ...props.modelValue.slice(index + 1)
  ]);
}
</script>
